import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import AiWriter from "@/components/admin/AiWriter";
import ImageUpload from "@/components/ImageUpload";

const EMPTY = { title: "", category: "", date: "", summary: "", image_url: "", featured: false, published: true };
const inputCls = "w-full rounded-lg border border-input bg-[#060B18] px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500";

export default function NewsTab() {
  const [articles, setArticles] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/news").then((r) => setArticles(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/news/${editing}`, form);
        toast.success("Article mis à jour");
      } else {
        await api.post("/admin/news", form);
        toast.success("Article publié");
      }
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/news/${id}`);
      toast.success("Article supprimé");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= articles.length) return;
    const a = articles[i];
    const b = articles[j];
    const payload = (x, position) => ({ title: x.title, category: x.category, date: x.date, summary: x.summary, image_url: x.image_url || "", featured: !!x.featured, position, published: x.published !== false });
    try {
      await api.put(`/admin/news/${a.id}`, payload(a, b.position ?? j + 1));
      await api.put(`/admin/news/${b.id}`, payload(b, a.position ?? i + 1));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid gap-6" data-testid="news-admin">
      <form onSubmit={submit} className="card-tactical grid gap-4 p-5" data-testid="admin-news-form">
        <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase">
          {editing ? <Pencil className="h-5 w-5 text-orange-500" /> : <Plus className="h-5 w-5 text-orange-500" />}
          {editing ? "Modifier l'article" : "Publier une actualité"}
        </p>
        <AiWriter kind="news" testid="admin-news-ai" context={{ category: form.category }}
          onUse={async ({ title, text }) => {
            setForm((f) => ({ ...f, title: title || f.title, summary: text }));
            toast.success("Article généré — vérifiez puis publiez");
          }} />
        <div className="grid gap-3 sm:grid-cols-3">
          <input placeholder="Titre" value={form.title} data-testid="admin-news-title"
            onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls + " sm:col-span-2"} required />
          <input placeholder="Catégorie (LoL, TFT, Boutique...)" value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} required />
          <input placeholder="Date (ex: 21 Juillet 2026)" value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} required />
          <textarea placeholder="Résumé de l'article (les sauts de ligne et paragraphes seront respectés)" value={form.summary}
            onChange={(e) => setForm({ ...form, summary: e.target.value })} className={inputCls + " sm:col-span-2"} rows={5} required />
          <div className="sm:col-span-3">
            <input placeholder="URL de l'illustration (optionnel)" value={form.image_url} data-testid="admin-news-image"
              onChange={(e) => setForm({ ...form, image_url: e.target.value })} className={inputCls} />
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <ImageUpload testid="admin-news-image-upload" onUploaded={(url) => setForm((f) => ({ ...f, image_url: url }))} />
              {form.image_url && (
                <img src={form.image_url} alt="Aperçu illustration" data-testid="admin-news-image-thumb"
                  className="h-16 rounded-lg border border-orange-500/20 object-cover" />
              )}
            </div>
          </div>
        </div>
        <label className="flex w-fit items-center gap-3 rounded-lg border border-orange-500/20 bg-orange-500/5 p-3 text-sm">
          <input type="checkbox" checked={form.featured} data-testid="admin-news-featured"
            onChange={(e) => setForm({ ...form, featured: e.target.checked })}
            className="h-4 w-4 accent-orange-500" />
          Mettre à la une — toujours affichée en premier (remplace l'actuelle)
        </label>
        <label className="flex w-fit items-center gap-3 rounded-lg border border-slate-600/60 bg-slate-500/5 p-3 text-sm">
          <input type="checkbox" checked={form.published} data-testid="admin-news-published"
            onChange={(e) => setForm({ ...form, published: e.target.checked })}
            className="h-4 w-4 accent-orange-500" />
          Publié — décocher pour garder l'article en brouillon (invisible sur le site)
        </label>
        <div className="flex gap-3">
          <button type="submit" data-testid="admin-news-submit"
            className="rounded-lg bg-orange-500 px-5 py-2.5 text-sm font-bold uppercase text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400">
            {editing ? "Enregistrer" : "Publier"}
          </button>
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm(EMPTY); }} data-testid="admin-news-cancel"
              className="rounded-lg border border-slate-600 px-5 py-2.5 text-sm font-bold uppercase text-slate-300">
              Annuler
            </button>
          )}
        </div>
      </form>

      <div className="grid gap-3">
        <p className="text-xs text-slate-500">Utilisez les flèches pour choisir l'ordre d'affichage. L'article « À la une » reste toujours en premier.</p>
        {articles.map((a, i) => (
          <div key={a.id} data-testid={`admin-news-row-${a.id}`} className="card-tactical flex flex-wrap items-center justify-between gap-3 p-4">
            <div>
              {a.published === false && (
                <span className="mr-2 rounded border border-slate-500/60 bg-slate-500/15 px-2 py-0.5 text-[11px] font-bold uppercase text-slate-300">Brouillon</span>
              )}
              {a.featured && (
                <span className="mr-2 rounded bg-orange-500 px-2 py-0.5 text-[11px] font-black uppercase text-[#060B18]">À la une</span>
              )}
              <span className="mr-2 rounded border border-orange-500/40 bg-orange-500/10 px-2 py-0.5 text-[11px] font-bold uppercase text-orange-400">{a.category}</span>
              <span className="font-heading text-lg font-bold uppercase">{a.title}</span>
              <span className="ml-2 text-sm text-slate-400">{a.date}</span>
            </div>
            <div className="flex gap-2">
              <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`admin-news-up-${a.id}`} aria-label="Monter l'article"
                className="rounded-lg border border-slate-600 p-2 text-slate-300 transition-colors hover:border-orange-500 hover:text-orange-400 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
              <button onClick={() => move(i, 1)} disabled={i === articles.length - 1} data-testid={`admin-news-down-${a.id}`} aria-label="Descendre l'article"
                className="rounded-lg border border-slate-600 p-2 text-slate-300 transition-colors hover:border-orange-500 hover:text-orange-400 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
              <button onClick={() => { setEditing(a.id); setForm({ title: a.title, category: a.category, date: a.date, summary: a.summary, image_url: a.image_url || "", featured: !!a.featured, published: a.published !== false, position: a.position ?? i + 1 }); }}
                data-testid={`admin-news-edit-${a.id}`}
                className="rounded-lg border border-orange-500/40 p-2 text-orange-400 hover:bg-orange-500/10"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => remove(a.id)} data-testid={`admin-news-delete-${a.id}`}
                className="rounded-lg border border-red-500/40 p-2 text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
